/**
 * Matter Feed Phase 0 web prototype
 * Access: /?test=matter-feed
 *
 * Click model: title + thumbnail open a light reader; card chrome is not a giant hit target.
 * Save = park in Feed (signal). Save to note = spawn garden doc. Different jobs.
 *
 * Bet: docs/product/bets/the-matter-feed-sovereign-curated-feed-via-flows.md
 */

import { useEffect, useMemo, useState } from 'react'
import {
  Bookmark,
  Check,
  ExternalLink,
  FilePlus,
  Inbox,
  ListTodo,
  Loader2,
  MessageSquare,
  MoreHorizontal,
  Settings2,
  ThumbsDown,
  ThumbsUp,
  X,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { testIds } from '@/lib/test-ids'
import {
  MaaterProtoShell,
  type MaaterProtoThemeId,
} from '@/components/prototype/maater-proto-shell'
import { ProtoAppHeader } from '@/components/prototype/maater-proto-chrome'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'

type TopicId = 'all' | 'learning' | 'professional' | 'health' | 'exploration'
type Signal = 'more' | 'less' | 'saved' | 'dismissed' | null

interface FeedItem {
  id: string
  title: string
  summary: string
  topic: Exclude<TopicId, 'all'>
  relativeTime: string
  sourceLabel: string
  sourceUrl: string
  flowSlug: string
  /** Optional - YouTube thumb / og:image when source has media */
  imageUrl?: string
  exploration?: boolean
}

const TOPICS: { id: TopicId; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'learning', label: 'Learning' },
  { id: 'professional', label: 'Professional' },
  { id: 'health', label: 'Health' },
  { id: 'exploration', label: 'Exploration' },
]

const BATCH_SIZE = 5

/** Real YT thumbs where we have video ids; omit image when source has none. */
const FIXTURES: FeedItem[] = [
  {
    id: 'f1',
    title: 'Macro: what IS-LM still explains (and what it does not)',
    summary:
      'Ten-minute explainer distilled from two YouTube digests. Focus: interest rates as a bridge between goods and money markets - not a crystal ball.',
    topic: 'learning',
    relativeTime: '22m',
    sourceLabel: 'YouTube research digest',
    sourceUrl: 'https://www.youtube.com/watch?v=9euKCrTyMEc',
    flowSlug: 'learning/macro-drip',
    imageUrl: 'https://i.ytimg.com/vi/9euKCrTyMEc/hqdefault.jpg',
  },
  {
    id: 'f2',
    title: 'Hop weekly: Basecamp blockers worth a glance',
    summary:
      'Three client threads with due dates this week. Not an Inbox dump - skim for context before triage.',
    topic: 'professional',
    relativeTime: '1h',
    sourceLabel: 'Basecamp recent activity',
    sourceUrl: 'https://3.basecamp.com/',
    flowSlug: 'work/hop-weekly-digest',
    // no image - text digest
  },
  {
    id: 'f3',
    title: 'Sleep debt vs circadian: one paper, one takeaway',
    summary:
      'Summary of a 2024 review: consistent wake time beats catching up on weekends for next-day focus. Source linked; no wellness fluff.',
    topic: 'health',
    relativeTime: '3h',
    sourceLabel: 'Longevity research board',
    sourceUrl: 'https://pubmed.ncbi.nlm.nih.gov/',
    flowSlug: 'health/longevity-weekly',
    imageUrl: 'https://i.ytimg.com/vi/D34KyceGhPE/hqdefault.jpg',
  },
  {
    id: 'f4',
    title: 'From your garden: ski trip notes from last March',
    summary:
      'Resurfaced workspace note tagged ski + destinations. Exploration budget pick - outside stated Learning topic.',
    topic: 'exploration',
    relativeTime: '5h',
    sourceLabel: 'documents.query',
    sourceUrl: '#',
    flowSlug: 'personal/resurface-related',
    exploration: true,
  },
  {
    id: 'f5',
    title: 'Trivia drip: Roman concrete and why it lasts',
    summary:
      'Sixty-second fact card. Save for later keeps it in Feed; Save to note puts it in the garden.',
    topic: 'learning',
    relativeTime: '6h',
    sourceLabel: 'ai.session microlearning',
    sourceUrl: '#',
    flowSlug: 'learning/trivia-history',
    imageUrl: 'https://i.ytimg.com/vi/JnlA9FXCu6w/hqdefault.jpg',
  },
  {
    id: 'f6',
    title: 'AI tooling: Cursor rules that stuck this month',
    summary:
      'Digest of three posts on agent skills vs prompts. Tuned to your Flows + Maater work context.',
    topic: 'professional',
    relativeTime: '1d',
    sourceLabel: 'YouTube + docs synth',
    sourceUrl: 'https://www.youtube.com/watch?v=A97sPctsnXU',
    flowSlug: 'work/ai-tooling-trends',
    imageUrl: 'https://i.ytimg.com/vi/A97sPctsnXU/hqdefault.jpg',
  },
  {
    id: 'f7',
    title: 'Zone 2: what "conversational pace" actually means',
    summary:
      'Short synthesis from a Huberman clip + one review paper. Card ends; no autoplay rabbit hole.',
    topic: 'health',
    relativeTime: '1d',
    sourceLabel: 'Health science digest',
    sourceUrl: 'https://www.youtube.com/watch?v=D34KyceGhPE',
    flowSlug: 'health/zone2-basics',
    imageUrl: 'https://i.ytimg.com/vi/D34KyceGhPE/hqdefault.jpg',
  },
  {
    id: 'f8',
    title: 'Feedly pause pattern: why an endpoint feels good',
    summary:
      'Competitive borrow for Matter Feed UX - checkbox after a batch. This card is meta on purpose (exploration).',
    topic: 'exploration',
    relativeTime: '2d',
    sourceLabel: 'Competitive research note',
    sourceUrl: '#',
    flowSlug: 'product/feed-ux-research',
    exploration: true,
  },
]

function MatterFeedTest() {
  const ids = testIds.matterFeedPrototype
  const [theme, setTheme] = useState<MaaterProtoThemeId>('paper')
  const [topic, setTopic] = useState<TopicId>('all')
  const [loading, setLoading] = useState(true)
  const [signals, setSignals] = useState<Record<string, Signal>>({})
  const [batchEnd, setBatchEnd] = useState(false)
  const [toast, setToast] = useState<string | null>(null)
  const [readerId, setReaderId] = useState<string | null>(null)

  useEffect(() => {
    const t = window.setTimeout(() => setLoading(false), 700)
    return () => window.clearTimeout(t)
  }, [])

  useEffect(() => {
    if (!toast) return
    const t = window.setTimeout(() => setToast(null), 2200)
    return () => window.clearTimeout(t)
  }, [toast])

  const visible = useMemo(() => {
    let items = FIXTURES.filter((item) => signals[item.id] !== 'dismissed')
    if (topic === 'exploration') {
      items = items.filter((i) => i.topic === 'exploration' || i.exploration)
    } else if (topic !== 'all') {
      items = items.filter((i) => i.topic === topic)
    }
    return items.slice(0, BATCH_SIZE)
  }, [topic, signals])

  const readerItem = readerId ? FIXTURES.find((i) => i.id === readerId) ?? null : null

  function setSignal(id: string, signal: Signal) {
    setSignals((prev) => ({ ...prev, [id]: signal }))
    if (signal === 'more') setToast('Signal: more like this (agent may propose a flow tweak)')
    if (signal === 'less') setToast('Signal: less like this')
    if (signal === 'saved') setToast('Saved for later - stays in Feed / ranking signal (not a new note)')
    if (signal === 'dismissed') {
      setToast('Dismissed from this batch')
      if (readerId === id) setReaderId(null)
    }
  }

  function spawn(kind: 'note' | 'todo' | 'source', item: FeedItem) {
    if (kind === 'source' && item.sourceUrl !== '#') {
      window.open(item.sourceUrl, '_blank', 'noopener,noreferrer')
      return
    }
    if (kind === 'note') setToast(`Save to note: would create a garden doc from "${item.title}"`)
    if (kind === 'todo') setToast(`Mock: create todo from "${item.title}"`)
    if (kind === 'source') setToast('Mock source (no external URL)')
  }

  return (
    <MaaterProtoShell theme={theme} className="min-h-screen">
      <div
        className="flex min-h-screen flex-col"
        style={{ background: 'var(--paper)', color: 'var(--ink)' }}
        data-testid={ids.root}
      >
        <ProtoAppHeader
          theme={theme}
          onTheme={setTheme}
          badge="feed"
          prototypeIndexTestId={ids.prototypeIndex}
          testId={ids.appHeader}
        />

        <div className="mx-auto flex w-full max-w-2xl flex-1 flex-col gap-4 px-4 py-5 sm:px-6">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h1 className="text-xl font-semibold tracking-tight" style={{ color: 'var(--ink)' }}>
                Matter Feed
              </h1>
              <p className="mt-1 text-sm" style={{ color: 'var(--ink-soft)' }}>
                Browse what compounds your goals - not Inbox
              </p>
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-9 w-9 shrink-0"
                  style={{ borderColor: 'var(--line)', background: 'var(--cream-card)' }}
                  data-testid={ids.feedMenuTrigger}
                  aria-label="Feed menu"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56" data-testid={ids.feedMenu}>
                <DropdownMenuLabel>Feed</DropdownMenuLabel>
                <DropdownMenuItem
                  onClick={() => setToast('Would open chat: "Help me refine this Feed..."')}
                  data-testid={ids.refineInChat}
                >
                  <MessageSquare className="mr-2 h-4 w-4" />
                  Refine Feed in chat
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setToast('Would open Feed Flows (topic recipes)')}
                >
                  <Settings2 className="mr-2 h-4 w-4" />
                  Manage topics / flows
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => setBatchEnd(true)}
                  disabled={batchEnd || loading}
                >
                  <Check className="mr-2 h-4 w-4" />
                  Enough for now
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div
            className="flex items-start gap-3 rounded-lg border px-3 py-2.5 text-sm"
            style={{
              borderColor: 'var(--line)',
              background: 'var(--cream-card)',
              color: 'var(--ink-soft)',
            }}
            data-testid={ids.vsInboxBanner}
          >
            <Inbox className="mt-0.5 h-4 w-4 shrink-0" style={{ color: 'var(--rust)' }} />
            <p>
              <span className="font-semibold" style={{ color: 'var(--ink)' }}>
                Title + thumb
              </span>{' '}
              open the card. Actions stay on the toolbar / ⋯ so the whole card is not a minefield.
              {' '}
              <span className="font-semibold" style={{ color: 'var(--ink)' }}>
                Save
              </span>{' '}
              parks in Feed;{' '}
              <span className="font-semibold" style={{ color: 'var(--ink)' }}>
                Save to note
              </span>{' '}
              writes a garden doc.
            </p>
          </div>

          <div className="flex flex-wrap gap-2" data-testid={ids.topicChips}>
            {TOPICS.map((t) => {
              const active = topic === t.id
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => {
                    setTopic(t.id)
                    setBatchEnd(false)
                    setReaderId(null)
                  }}
                  className="rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors"
                  style={{
                    borderColor: active ? 'var(--rust)' : 'var(--line)',
                    background: active ? 'var(--rust-pale)' : 'var(--cream-card)',
                    color: active ? 'var(--rust)' : 'var(--ink-soft)',
                    boxShadow: active ? '0 0 0 2px var(--paper), 0 0 0 4px var(--rust)' : undefined,
                  }}
                  data-testid={ids.topicChip(t.id)}
                >
                  {t.label}
                </button>
              )
            })}
          </div>

          <div className="flex items-center justify-between gap-2 text-xs" style={{ color: 'var(--ink-faint)' }}>
            <span data-testid={ids.batchMeta}>
              {loading ? 'Loading batch...' : batchEnd ? 'Batch complete' : `${visible.length} of ~${BATCH_SIZE} in this batch`}
            </span>
            {!loading && !batchEnd && visible.length > 0 ? (
              <button
                type="button"
                className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 font-semibold"
                style={{
                  borderColor: 'var(--line)',
                  background: 'var(--paper-raised)',
                  color: 'var(--ink)',
                }}
                onClick={() => setBatchEnd(true)}
                data-testid={ids.enoughButton}
              >
                <Check className="h-3.5 w-3.5" style={{ color: 'var(--rust)' }} />
                Enough for now
              </button>
            ) : null}
          </div>

          {readerItem && !batchEnd ? (
            <div
              className="rounded-xl border p-4"
              style={{ borderColor: 'var(--rust)', background: 'var(--cream-card)' }}
              data-testid={ids.reader}
            >
              <div className="mb-3 flex items-center justify-between gap-2">
                <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--ink-faint)' }}>
                  Reader (prototype)
                </p>
                <button
                  type="button"
                  className="text-xs font-semibold underline-offset-2 hover:underline"
                  style={{ color: 'var(--rust)' }}
                  onClick={() => setReaderId(null)}
                  data-testid={ids.readerClose}
                >
                  Back to Feed
                </button>
              </div>
              {readerItem.imageUrl ? (
                <img
                  src={readerItem.imageUrl}
                  alt=""
                  className="mb-3 h-40 w-full rounded-lg object-cover"
                />
              ) : null}
              <h2 className="text-lg font-semibold">{readerItem.title}</h2>
              <p className="mt-2 text-sm leading-relaxed" style={{ color: 'var(--ink-soft)' }}>
                {readerItem.summary}
              </p>
              <p className="mt-3 font-mono text-[11px]" style={{ color: 'var(--ink-faint)' }}>
                {readerItem.sourceLabel} · flow:{readerItem.flowSlug}
              </p>
            </div>
          ) : null}

          <div className="flex flex-col gap-3" data-testid={ids.cardList}>
            {loading
              ? Array.from({ length: 4 }).map((_, i) => (
                  <div
                    key={i}
                    className="animate-pulse rounded-xl border p-3"
                    style={{
                      borderColor: 'var(--line)',
                      background: 'var(--cream-card)',
                      minHeight: 112,
                    }}
                    data-testid={ids.skeleton}
                  >
                    <div className="flex gap-3">
                      <div className="h-20 w-28 shrink-0 rounded-lg" style={{ background: 'var(--paper-deep)' }} />
                      <div className="min-w-0 flex-1">
                        <div className="mb-2 h-3 w-20 rounded" style={{ background: 'var(--paper-deep)' }} />
                        <div className="mb-2 h-4 rounded" style={{ width: '90%', background: 'var(--paper-deep)' }} />
                        <div className="h-3 rounded" style={{ width: '70%', background: 'var(--paper-raised)' }} />
                      </div>
                    </div>
                  </div>
                ))
              : null}

            {!loading && batchEnd ? (
              <div
                className="rounded-xl border px-4 py-10 text-center"
                style={{ borderColor: 'var(--line)', background: 'var(--cream-card)' }}
                data-testid={ids.batchComplete}
              >
                <p className="text-base font-semibold" style={{ color: 'var(--ink)' }}>
                  Enough for now
                </p>
                <p className="mt-2 text-sm" style={{ color: 'var(--ink-soft)' }}>
                  Endpoint on purpose - Feedly-style friction. Come back later or switch topics.
                </p>
                <button
                  type="button"
                  className="mt-4 text-sm font-semibold underline-offset-2 hover:underline"
                  style={{ color: 'var(--rust)' }}
                  onClick={() => setBatchEnd(false)}
                >
                  Show this batch again
                </button>
              </div>
            ) : null}

            {!loading && !batchEnd && visible.length === 0 ? (
              <div
                className="rounded-xl border px-4 py-10 text-center"
                style={{ borderColor: 'var(--line)', background: 'var(--cream-card)' }}
                data-testid={ids.emptyState}
              >
                <p className="text-base font-semibold">Nothing in this topic right now</p>
                <p className="mt-2 text-sm" style={{ color: 'var(--ink-soft)' }}>
                  Feed Flows will fill this when they run. Try All or Learning.
                </p>
              </div>
            ) : null}

            {!loading &&
              !batchEnd &&
              visible.map((item) => {
                const sig = signals[item.id] ?? null
                const open = () => setReaderId(item.id)
                return (
                  <article
                    key={item.id}
                    className="rounded-xl border p-3"
                    style={{
                      borderColor: readerId === item.id ? 'var(--rust)' : 'var(--line)',
                      background: 'var(--cream-card)',
                      opacity: sig === 'less' ? 0.72 : 1,
                    }}
                    data-testid={ids.card(item.id)}
                  >
                    <div className="flex gap-3">
                      {item.imageUrl ? (
                        <button
                          type="button"
                          onClick={open}
                          className="group relative h-20 w-28 shrink-0 overflow-hidden rounded-lg border focus-visible:outline-none focus-visible:ring-2"
                          style={{ borderColor: 'var(--line)', ['--tw-ring-color' as string]: 'var(--rust)' }}
                          data-testid={ids.thumb(item.id)}
                          aria-label={`Open ${item.title}`}
                        >
                          <img
                            src={item.imageUrl}
                            alt=""
                            className="h-full w-full object-cover transition-opacity group-hover:opacity-90"
                          />
                        </button>
                      ) : null}

                      <div className="min-w-0 flex-1">
                        <div
                          className="mb-1.5 flex flex-wrap items-center gap-2 text-[11px] font-semibold uppercase tracking-wide"
                          style={{ color: 'var(--ink-faint)' }}
                        >
                          <span
                            className="rounded-full px-2 py-0.5"
                            style={{ background: 'var(--rust-pale)', color: 'var(--rust)' }}
                          >
                            {item.topic}
                          </span>
                          {item.exploration ? (
                            <span className="rounded-full border px-2 py-0.5" style={{ borderColor: 'var(--line)' }}>
                              Exploration
                            </span>
                          ) : null}
                          <span>{item.relativeTime}</span>
                          {!item.imageUrl ? (
                            <span className="normal-case tracking-normal font-medium">No media</span>
                          ) : null}
                        </div>

                        <button
                          type="button"
                          onClick={open}
                          className="text-left text-base font-semibold leading-snug hover:underline focus-visible:underline"
                          style={{ color: 'var(--ink)' }}
                          data-testid={ids.cardTitle(item.id)}
                        >
                          {item.title}
                        </button>

                        <p className="mt-1.5 line-clamp-2 text-sm leading-relaxed" style={{ color: 'var(--ink-soft)' }}>
                          {item.summary}
                        </p>

                        <p
                          className="mt-2 font-mono text-[11px]"
                          style={{ color: 'var(--ink-faint)' }}
                          data-testid={ids.provenance(item.id)}
                        >
                          {item.sourceLabel} · flow:{item.flowSlug}
                        </p>
                      </div>
                    </div>

                    <div className="mt-3 flex flex-wrap items-center gap-2 border-t pt-3" style={{ borderColor: 'var(--line)' }}>
                      <button
                        type="button"
                        title="More like this"
                        className={cn('inline-flex h-8 w-8 items-center justify-center rounded-md border')}
                        style={{
                          borderColor: 'var(--line)',
                          background: sig === 'more' ? 'var(--rust-pale)' : 'var(--paper)',
                          color: 'var(--ink)',
                        }}
                        onClick={() => setSignal(item.id, 'more')}
                        data-testid={ids.signalMore(item.id)}
                      >
                        <ThumbsUp className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        title="Less like this"
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md border"
                        style={{
                          borderColor: 'var(--line)',
                          background: sig === 'less' ? 'var(--paper-deep)' : 'var(--paper)',
                        }}
                        onClick={() => setSignal(item.id, 'less')}
                        data-testid={ids.signalLess(item.id)}
                      >
                        <ThumbsDown className="h-3.5 w-3.5" />
                      </button>

                      <button
                        type="button"
                        title="Save for later (Feed signal - not a note)"
                        className="inline-flex h-8 items-center gap-1 rounded-md border px-2 text-xs font-medium"
                        style={{
                          borderColor: 'var(--line)',
                          background: sig === 'saved' ? 'var(--rust-pale)' : 'var(--paper)',
                          color: sig === 'saved' ? 'var(--rust)' : 'var(--ink)',
                        }}
                        onClick={() => setSignal(item.id, 'saved')}
                        data-testid={ids.signalSave(item.id)}
                      >
                        <Bookmark className="h-3.5 w-3.5" />
                        Save
                      </button>

                      <div className="ml-auto">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="h-8 w-8"
                              style={{ borderColor: 'var(--line)', background: 'var(--paper)' }}
                              data-testid={ids.cardMenuTrigger(item.id)}
                              aria-label="More actions"
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-52" data-testid={ids.cardMenu(item.id)}>
                            <DropdownMenuItem
                              onClick={() => spawn('source', item)}
                              data-testid={ids.spawnSource(item.id)}
                            >
                              <ExternalLink className="mr-2 h-4 w-4" />
                              Open source
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => spawn('note', item)}
                              data-testid={ids.spawnNote(item.id)}
                            >
                              <FilePlus className="mr-2 h-4 w-4" />
                              Save to note
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => spawn('todo', item)}
                              data-testid={ids.spawnTodo(item.id)}
                            >
                              <ListTodo className="mr-2 h-4 w-4" />
                              Create todo
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => setSignal(item.id, 'dismissed')}
                              data-testid={ids.signalDismiss(item.id)}
                            >
                              <X className="mr-2 h-4 w-4" />
                              Dismiss
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </article>
                )
              })}
          </div>

          {loading ? (
            <div className="flex items-center justify-center gap-2 py-2 text-xs" style={{ color: 'var(--ink-faint)' }}>
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Settling Feed batch...
            </div>
          ) : null}
        </div>

        {toast ? (
          <div
            className="fixed bottom-6 left-1/2 z-50 max-w-sm -translate-x-1/2 rounded-lg border px-4 py-2 text-center text-sm shadow-lg"
            style={{
              borderColor: 'var(--line)',
              background: 'var(--cream-card)',
              color: 'var(--ink)',
            }}
            data-testid={ids.toast}
          >
            {toast}
          </div>
        ) : null}
      </div>
    </MaaterProtoShell>
  )
}

export { MatterFeedTest }
export default MatterFeedTest
